import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Save, X } from 'lucide-react';
import { invalidateSpeakerPortraits } from '../hooks/useSpeakerPortrait';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline speaker-name editor for admins.
 *
 * Rename ONLY. If the new name collides with an existing speaker, the
 * backend returns 409 { code: 'name_conflict' } and we surface a toast
 * telling the admin to pick a different name. There is no merge action
 * anywhere in the product — every speaker stands on its own.
 */
const SpeakerNameEditor = ({
  name,
  onRenamed,
  className = '',
  buttonClassName = '',
  disabled = false,
  stopPropagation = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);

  const start = (e) => {
    if (stopPropagation) { e.stopPropagation(); e.preventDefault(); }
    if (disabled) return;
    setValue(name);
    setEditing(true);
  };

  const cancel = () => { setEditing(false); setValue(name); };

  const submit = async () => {
    const trimmed = (value || '').trim().replace(/\s+/g, ' ');
    if (!trimmed) { cancel(); return; }
    if (trimmed === name) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/speakers/${encodeURIComponent(name)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_name: trimmed }),
      });
      if (res.status === 409) {
        const body = await res.json();
        const msg = body?.detail?.message
          || `"${trimmed}" already exists — pick a different name.`;
        toast.error(msg, { duration: 6000 });
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      toast.success(`Renamed to ${data.name}`);
      setEditing(false);
      invalidateSpeakerPortraits();
      onRenamed?.(data.name);
    } catch (err) {
      toast.error(`Rename failed: ${(err.message || '').slice(0, 120)}`);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        className="flex items-center gap-1 justify-center w-full"
        onClick={(e) => stopPropagation && e.stopPropagation()}
        role="group"
        aria-label="Rename speaker"
      >
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') cancel();
          }}
          onClick={(e) => stopPropagation && e.stopPropagation()}
          className="min-w-0 flex-1 px-2 py-0.5 text-xs sm:text-sm rounded border border-[#9a7b24] text-[#0f1729] bg-white text-center focus:outline-none"
          data-testid={`speaker-name-input-${name}`}
        />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); submit(); }}
          disabled={saving}
          className="text-[#9a7b24] hover:text-[#7d6119] disabled:opacity-50 p-0.5"
          aria-label="Save name"
          data-testid={`speaker-name-save-${name}`}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); cancel(); }}
          className="text-[#8a8f99] hover:text-[#0f1729] p-0.5"
          aria-label="Cancel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      className={`group/name inline-flex items-center gap-1 max-w-full text-center ${buttonClassName} disabled:cursor-default`}
      title={disabled ? undefined : 'Click to rename'}
      data-testid={`speaker-name-edit-${name}`}
    >
      <span className={className}>{name}</span>
      {!disabled && (
        <Pencil className="w-3 h-3 text-[#9a7b24]/70 opacity-0 group-hover/name:opacity-100 transition-opacity shrink-0" />
      )}
    </button>
  );
};

export default SpeakerNameEditor;
